// Diagnostics / first-run screen: store overview + connection status,
// opportunity keyword count, and a publish-funnel summary. Friendly empty and
// not-connected states throughout (Polaris Page/Layout/Card/EmptyState).

import {
  Badge,
  BlockStack,
  Card,
  EmptyState,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  Text,
} from "@shopify/polaris";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../api/hooks";
import { useStore } from "../context/StoreProvider";
import { AsyncSection } from "../components/StateViews";
import { StatusBadge } from "../components/StatusBadge";
import { titleCase } from "../lib/format";

const OPP_PREVIEW_LIMIT = 100;

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="span" tone="subdued" variant="bodySm">
          {label}
        </Text>
        <Text as="span" variant="headingXl">
          {value}
        </Text>
        {hint ? (
          <Text as="span" tone="subdued" variant="bodySm">
            {hint}
          </Text>
        ) : null}
      </BlockStack>
    </Card>
  );
}

export function DashboardPage() {
  const api = useApi();
  const { store } = useStore();

  const opportunities = useAsync(
    () => api.listOpportunities({ store_id: store.id, limit: OPP_PREVIEW_LIMIT }),
    [api, store.id],
  );
  const funnel = useAsync(() => api.getPublishFunnel(store.id), [api, store.id]);

  const oppCount = opportunities.data
    ? `${opportunities.data.items.length}${opportunities.data.next_cursor ? "+" : ""}`
    : "—";

  return (
    <Page title="Overview" subtitle="Your SEO autopilot at a glance">
      <Layout>
        {/* Store overview */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {store.name || store.shop_domain}
                </Text>
                <StatusBadge status={store.status} />
              </InlineStack>
              <Text as="p" tone="subdued">
                {store.primary_domain ?? store.shop_domain} · {titleCase(store.platform)}
              </Text>
              <InlineStack gap="200" wrap>
                {store.connections.length === 0 ? (
                  <Text as="span" tone="subdued">
                    No external connections yet.
                  </Text>
                ) : (
                  store.connections.map((c) => (
                    <InlineStack key={c.provider} gap="100" blockAlign="center">
                      <Text as="span" variant="bodySm">
                        {titleCase(c.provider)}
                      </Text>
                      <StatusBadge status={c.status} />
                    </InlineStack>
                  ))
                )}
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Key numbers */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
            <AsyncSection
              loading={opportunities.loading}
              error={opportunities.error}
              data={opportunities.data}
              onRetry={opportunities.reload}
              loadingLabel="Loading opportunities"
            >
              {(data) => (
                <StatTile
                  label="Opportunity keywords"
                  value={oppCount}
                  hint={
                    data.items.length === 0
                      ? "No scored keywords yet — they appear after the first GSC sync."
                      : "Keywords with a computed opportunity score, ready to plan into content."
                  }
                />
              )}
            </AsyncSection>

            <AsyncSection
              loading={funnel.loading}
              error={funnel.error}
              data={funnel.data}
              onRetry={funnel.reload}
              loadingLabel="Loading publish funnel"
            >
              {(data) => (
                <StatTile
                  label="Content in the pipeline"
                  value={String(data.total)}
                  hint="Publish targets across all pipeline stages."
                />
              )}
            </AsyncSection>
          </InlineGrid>
        </Layout.Section>

        {/* Publish funnel breakdown */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Publish funnel
              </Text>
              <AsyncSection
                loading={funnel.loading}
                error={funnel.error}
                data={funnel.data}
                onRetry={funnel.reload}
                isEmpty={(d) => d.total === 0}
                empty={
                  <EmptyState
                    heading="Nothing published yet"
                    image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
                  >
                    <p>
                      Once RankVine drafts content, its progress from draft to verified shows up
                      here.
                    </p>
                  </EmptyState>
                }
                loadingLabel="Loading publish funnel"
              >
                {(data) => (
                  <InlineStack gap="200" wrap>
                    {Object.entries(data.funnel)
                      .filter(([, count]) => count > 0)
                      .map(([stage, count]) => (
                        <InlineStack key={stage} gap="100" blockAlign="center">
                          <StatusBadge status={stage} />
                          <Badge tone="new">{String(count)}</Badge>
                        </InlineStack>
                      ))}
                  </InlineStack>
                )}
              </AsyncSection>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
